import {
  Autocomplete,
  Box,
  Menu,
  MenuItem,
  Popover,
  TextField,
} from "@mui/material";
import * as d3 from "d3";
import { OrgChart as D3OrgChart } from "d3-org-chart";
import { Theme } from "klayowebshared";
import React, { Component } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import CenterIcon from "../../resources/icons-center.svg";
import OrgChartIcon from "../../resources/icons-org-chart.svg";
import SearchIcon from "../../resources/icons-search.svg";
import WarningIcon from "../../resources/icons-warning.svg";
import ZoomInIcon from "../../resources/icons-zoomin.svg";
import ZoomOutIcon from "../../resources/icons-zoomout.svg";

import ButtonGroup from "../buttons/ButtonGroup";
import Placeholder from "../layouts/Placeholder";
import Tooltip from "../Tooltip";
import { loadAvatar, subscribeAvatars } from "../../common/avatarStore";
import OrgChartButton from "./OrgChartButton";
import OrgChartCompanyNode from "./OrgChartCompanyNode";
import OrgChartPersonNode from "./OrgChartPersonNode";

import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";

interface EmployeeOption {
  employeeId: string;
  fullName: string;
  jobTitle?: string;
}

interface OrgChartTranslations {
  compliant?: string;
  nonCompliant?: string;
  orgChartNotAvailable?: string;
  orgChartNotAvailableDescription?: string;
  incompatibleBrowser?: string;
  incompatibleBrowserDescription?: string;
  searchEmployees?: string;
  removeHighlight?: string;
  highlightConnections?: string;
  viewEmployeeProfile?: string;
}

export default class OrgChart extends Component<any, any> {
  containerRef: any;
  zoomInRef: any;
  zoomOutRef: any;
  searchRef: any;
  resetRef: any;
  zoom: any;
  center: any;
  centering: any;
  firstDraw: any;
  lastX: any;
  lastY: any;
  isSafari: any;
  orgChart: any;
  unsubscribeAvatars: (() => void) | null = null;
  avatarRedrawTimer: ReturnType<typeof setTimeout> | undefined;
  searchResults: any;
  search: any;

  static defaultProps = {
    minZoom: 0.2,
    maxZoom: 2,
    nodeWidth: 260,
    translations: {
      compliant: "Compliant",
      nonCompliant: "Non-compliant",
      orgChartNotAvailable: "Org chart not available",
      orgChartNotAvailableDescription: "Create some employees and assign them jobs to generate the org chart",
      incompatibleBrowser: "Incompatible browser",
      incompatibleBrowserDescription: "This feature doesn't work on Safari. Please use a different browser",
      searchEmployees: "Search employees...",
      removeHighlight: "Remove highlight",
      highlightConnections: "Highlight connections",
      viewEmployeeProfile: "View employee profile",
    } as OrgChartTranslations,
  };

  constructor(props: any) {
    super(props);

    this.state = {
      actionEmployee: null,
      actionTarget: null,
      centerOn: props.centerOn,
      currentUser: props.currentUser,
      tooltipText: null,
      tooltipOffset: { x: 0, y: 0 },
      tooltipTarget: null,
      searchAnchorEl: null,
      searchValue: "",
      selectedEmployee: null,
    };

    this.containerRef = React.createRef();
    this.zoomInRef = React.createRef();
    this.zoomOutRef = React.createRef();
    this.resetRef = React.createRef();
    this.searchRef = React.createRef();
    this.zoom = 1;
    this.center = { x: 0, y: 0 };
    this.centering = true;
    this.firstDraw = true;
    this.lastX = 0;
    this.lastY = 0;

    //Temp until safari bugs fixed (can't target specific behaviour foreignObject overflow)
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    window.addEventListener("resize", this.onWindowResize.bind(this));
  }

  componentDidMount() {
    // Nodes are static markup, so a photo that finishes loading needs a redraw. Batch them.
    this.unsubscribeAvatars = subscribeAvatars((employeeId) => {
      if (!this.orgChart || !this.props.data?.orgTree || !this.getEmployee(employeeId)) return;
      clearTimeout(this.avatarRedrawTimer);
      this.avatarRedrawTimer = setTimeout(() => this.orgChart?.render(), 250);
    });
  }

  componentWillUnmount() {
    this.unsubscribeAvatars?.();
    clearTimeout(this.avatarRedrawTimer);
  }

  componentDidUpdate(prevProps: any) {
    //if (this.props.data !== prevProps.data) this.setState({ data: this.props.data });
    if (this.props.centerOn !== prevProps.centerOn)
      this.setState({ centerOn: this.props.centerOn });
    if (this.props.search !== prevProps.search)
      this.onSearch(this.props.search);
    if (this.props.organization !== prevProps.organization) this.forceUpdate();
  }

  onWindowResize(e: any) {
    this.onReset();
  }

  onSearch(search: any) {
    const { data, searchFilter } = this.props;

    if (!search || search === "") {
      this.searchResults = null;
      this.forceUpdate();
      return;
    }

    this.search = search;

    if (this.search && searchFilter) {
      this.searchResults = data.orgTree.filter((n: any) =>
        searchFilter(n, search)
      );
    }
  }
  handleSearchClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ searchAnchorEl: event.currentTarget });
  };

  handleSearchClose = () => {
    this.setState({ searchAnchorEl: null });
  };
  handleEmployeeSelect = (event: any, employee: EmployeeOption | null) => {
    if (employee) {
      // Clear previous highlights
      const { data } = this.props;
      if (data) {
        data.orgTree.forEach((emp: any) => {
          if (emp.highlight) {
            this.setHighlightSearch(emp.employeeId, false);
          }
        });
      }

      // Highlight and focus on selected employee
      this.setHighlightSearch(employee.employeeId, true);
      this.focusOnEmployee(employee.employeeId);
    }
    this.setState({ selectedEmployee: employee });
  };
  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState({ searchValue: value });

    const { data, searchFilter } = this.props;
    if (!value || !data || !searchFilter) {
      // Clear all highlights when search is empty
      if (data) {
        data.orgTree.forEach((emp: any) => {
          if (emp.highlight) {
            this.setHighlightSearch(emp.employeeId, false);
          }
        });
      }
      this.searchResults = null;
      this.forceUpdate();
      return;
    }
    this.forceUpdate();
  };
  renderOrgChart() {
    if (!this.containerRef || !this.containerRef.current) return;

    const { data, height, minZoom, maxZoom, currentUser, nodeWidth } =
      this.props;
    const { centerOn } = this.state;

    if (!data) return;

    const orgChart = this;

    if (!this.orgChart) {
      this.orgChart = new D3OrgChart()
        .container(this.containerRef.current)
        .data(data.orgTree)
        .layout("top");

      const origZoom = this.orgChart.zoomed;

      this.orgChart.zoomed = function (e: any, d: any) {
        orgChart.onZoom(e, e.transform.k);
        orgChart.onPan(e, e.transform.x, e.transform.y);

        /*if(e.sourceEvent instanceof MouseEvent) {

                    const xDiff = Math.abs(e.transform.x - orgChart.lastX);
                    const yDiff = Math.abs(e.transform.y - orgChart.lastY);

                    if(xDiff > 2 || yDiff > 2) {
                        origZoom.call(this, e, d);

                        orgChart.lastX = e.transform.x;
                        orgChart.lastY = e.transform.y;
                    }
                }
                else*/
        origZoom.call(this, e, d);
      };

      this.orgChart.zoomTreeBounds = function ({
        x0,
        x1,
        y0,
        y1,
        params = { animate: true, scale: true },
      }: any) {
        if (!orgChart.containerRef || !orgChart.containerRef.current) return;
        const {
          centerG,
          /*svgWidth: w, svgHeight: h, */ svg,
          zoomBehavior,
          duration,
          lastTransform,
        } = this.getChartState();
        const w = orgChart.containerRef.current.clientWidth;
        const h = orgChart.containerRef.current.clientHeight;
        let scaleVal = Math.min(
          8,
          0.9 / Math.max((x1 - x0) / w, (y1 - y0) / h)
        );
        let identity = d3.zoomIdentity.translate(w / 2, h / 2);
        identity = identity.scale(params.scale ? scaleVal : lastTransform.k);
        const offsetY = (orgChart.containerRef.current.clientHeight / 100) * 15;
        identity = identity.translate(-(x0 + x1) / 2, -(y0 + y1) / 2 - offsetY);
        svg
          .transition()
          .duration(params.animate ? duration : 0)
          .call(zoomBehavior.transform, identity);
        centerG
          .transition()
          .duration(params.animate ? duration : 0)
          .attr("transform", "translate(0,0)");
      };

      const origSetExpansionFlagToChildren =
        this.orgChart.setExpansionFlagToChildren;
      this.orgChart.setExpansionFlagToChildren = function (
        d: any,
        expand: any
      ) {
        if (d.data._expanded === undefined) return;
        //if(!d.data._expanded)
        origSetExpansionFlagToChildren.call(this, d, expand);
      };

      /*const origonButtonClick = this.orgChart.onButtonClick;
            this.orgChart.onButtonClick = function(e, d) {
                e.preventDefault();
                e.stopPropagation();
                origonButtonClick.call(this, e, d);
            }*/

      setTimeout((t: any) => (this.centering = false), 1000);
    }

    this.orgChart = this.orgChart
      .svgHeight(orgChart.containerRef.current.clientHeight)
      .compact(false)
      .scaleExtent([minZoom, maxZoom])
      .parentNodeId((d: any) => d.parentId)
      .nodeHeight((d: any) => {
        if (d.data.type === "person") return 152;
        else if (d.data.type === "company") return 124;
        return 85;
      })
      .nodeWidth((d: any) => {
        return nodeWidth;
      })
      .childrenMargin((d: any) => 80)
      .compactMarginBetween((d: any) => 25)
      .compactMarginPair((d: any) => 50)
      .neighbourMargin((a: any, b: any) => 25)
      .siblingsMargin((d: any) => 25)
      .buttonContent(this.renderButton.bind(this))
      .linkUpdate(function (d: any, i: any, arr: any) {
        orgChart.linkUpdate(this, d, i, arr);
      })
      .nodeContent(this.renderNode.bind(this))
      .nodeUpdate(function (d: any, i: any, arr: any) {
        orgChart.nodeUpdate(this, d, i, arr);
      });

    //this.orgChart.setLayouts({ expandNodesFirst: false });

    //this.orgChart.setExpanded('73c94c77-b11b-4bf3-a9a2-450de95b530a').render();
    if (this.firstDraw && currentUser) {
      this.firstDraw = false;

      this.orgChart.setLayouts({ expandNodesFirst: false });

      this.orgChart = this.orgChart
        .setExpanded("org-root", true)
        .setExpanded("org-root-job", true);

      data.orgTree
        .filter((e: any) => e.parentId === "org-root-job")
        .forEach(
          (e: any) =>
            (this.orgChart = this.orgChart.setExpanded(e.employeeId, true))
        );

      const curUser = data.orgTree.find((e: any) => e.id === currentUser);
      if (curUser) {
        curUser.highlight = true;
        //curUser._expanded = true;

        this.orgChart = this.orgChart
          .setExpanded(currentUser, true)
          .setCentered(currentUser);

        data.orgTree
          .filter((e: any) => e.parentId === currentUser)
          .forEach(
            (e: any) =>
              (this.orgChart = this.orgChart.setExpanded(e.employeeId, true))
          );
      } else this.orgChart = this.orgChart.setCentered("org-root-job");

      /*setTimeout(t => {

                this.orgChart = this.orgChart
                    .setExpanded("org-root", true)
                    .setExpanded("org-root-job", true)
                    .setExpanded(currentUser, true)
                    .render();

            }, 5000);*/

      //this.setExpanded(currentUser, true);

      /*this.orgChart = this.orgChart
                //.setHighlighted(currentUser)
                .setCentered(currentUser)
                .setExpanded(currentUser, true)
            //.setUpToTheRootHighlighted(currentUser);*/

      data.orgTree
        .filter((e: any) => e.parentId === currentUser)
        .forEach(
          (e: any) =>
            (this.orgChart = this.orgChart.setExpanded(e.employeeId, true))
        );
    }

    this.orgChart = this.orgChart.render();
  }

  onDrag(e: any) {}

  onZoom(e: any, zoom: any) {
    const { minZoom, maxZoom } = this.props;

    this.zoom = zoom;

    if (this.zoomOutRef && this.zoomOutRef.current)
      this.zoomOutRef.current.setEnabled(zoom > minZoom);
    if (this.zoomInRef && this.zoomInRef.current)
      this.zoomInRef.current.setEnabled(zoom < maxZoom);
  }

  onPan(e: any, x: any, y: any) {
    if (!this.centering && this.resetRef)
      this.resetRef.current.setEnabled(
        x !== this.center.x || y !== this.center.y
      );
  }

  centerOnOrg() {
    const { nodeWidth } = this.props;

    this.orgChart.zoomTreeBounds({
      params: { animate: true, scale: false },
      x0: -nodeWidth,
      x1: 0,
      y0: (this.containerRef.current.clientHeight / 100) * 10,
      y1: this.containerRef.current.clientHeight,
    });
  }

  onReset(e?: any) {
    if (!this.orgChart) return;
    const { currentUser, data } = this.props;
    const { svg, zoomBehavior } = this.orgChart.getChartState();

    // Guard against svg being undefined during initialization or unmounting
    if (!svg || !zoomBehavior) return;

    svg.transition().duration(0).call(zoomBehavior.scaleTo, 1);

    const curUser = data.orgTree.find((e: any) => e.id === currentUser);

    setTimeout((t: any) => {
      this.orgChart = this.orgChart
        .setExpanded("org-root", true)
        .setExpanded("org-root-job", true);

      data.orgTree
        .filter((e: any) => e.parentId === "org-root-job")
        .forEach(
          (e: any) =>
            (this.orgChart = this.orgChart.setExpanded(e.employeeId, true))
        );

      this.orgChart = this.orgChart
        .setExpanded(curUser ? currentUser : "org-root-job", true)
        .setCentered(curUser ? currentUser : "org-root-job");

      this.orgChart.fit({
        animate: true,
        scale: false,
        nodes: [this.getNode(curUser ? currentUser : "org-root-job")],
      });

      //this.orgChart = this.orgChart
      //.setExpanded(curUser ? currentUser : 'org-root-job', true)
      //.setCentered(curUser ? currentUser : 'org-root-job')
      //.render();
    }, 100);

    this.centering = true;

    setTimeout((t: any) => {
      this.centering = false;
      this.center = this.orgChart.getChartState().lastTransform;
    }, 500);

    if (this.resetRef && this.resetRef.current)
      this.resetRef.current.setEnabled(false);
  }
  centerOn(employeeId: any) {
    try {
      if (!this.orgChart) return;
      this.orgChart = this.orgChart.setCentered(employeeId).render();

      let drag = d3
        .drag()
        .on("start", (event, d) => {})
        .on("drag", (event, d) => {})
        .on("end", (event, d) => {});

      let { svg } = this.orgChart.getChartState();
      svg = svg.call(drag);
    } catch (e) {}
  }

  focusOnEmployee = (employeeId: string) => {
    try {
      if (!this.orgChart) return;

      // Expand the node and its parents
      this.setExpanded(employeeId, true);
      const employee = this.getEmployee(employeeId);
      if (employee) {
        this.expandAllParents(employee);
      }

      const node = this.getNode(employeeId);
      if (!node) return;

      //fit method to center on the node without scaling
      this.orgChart.fit({
        nodes: [node],
        animate: true,
        scale: false,
        centerMind: true,
        duration: 500,
      });

      this.forceUpdate();
    } catch (e) {
      console.error("Error in focusOnEmployee:", e);
    }
  };
  nodeUpdate(node: any, d: any, i: any, arr: any) {
    const nodeSelect = d3.select(node);

    if (d.data.type === "company") {
      nodeSelect.select(".node-button-g").attr("display", "none");
    }

    nodeSelect
      .select(".node-button-g")
      .style("user-select", "none")
      .on("mousedown", (e, d) => e.stopPropagation());

    nodeSelect
      .select(".node-foreign-object-div")
      .style("opacity", (d: any) =>
        !this.searchResults || this.searchResults.includes(d.data) ? "1" : "0.2"
      )
      .style("filter", (d: any) =>
        !this.searchResults || this.searchResults.includes(d.data)
          ? "none"
          : "grayscale(1)"
      );

    /*nodeSelect
            .select('.node-rect')
            .attr("stroke", d => d.data.highlight || d.data._upToTheRootHighlighted ? '#152785' : 'none')
            .attr("stroke-width", d.data.highlight || d.data._upToTheRootHighlighted ? 10 : 1);*/

    nodeSelect.select(".node-button-rect").attr("width", 54);

    const foreignObjSel = nodeSelect
      .select(".node-button-foreign-object")
      .attr("x", -26)
      .attr("y", -18)
      .attr("width", 54);

    foreignObjSel.select(".node-button-div").style("pointer-events", "all");

    nodeSelect
      .select(".klayo-orgchart_node-morebutton")
      .style("user-select", "none")
      .on("mousedown", (e, d) => e.stopPropagation())
      .on("click", (e) => this.onMore(e, node, d));

    nodeSelect
      .select(".klayo_profilepic_badge")
      .on("mouseenter", (e) => this.onCompliantBadgeMouseEnter(e, node, d))
      .on("mouseleave", (e) => this.onCompliantBadgeMouseLeave(e, node, d));
  }

  onCompliantBadgeMouseEnter(e: any, node: any, d: any) {
    const { translations } = this.props;
    this.setState({
      tooltipText: d.data.isCompliant ? translations.compliant : translations.nonCompliant,
      tooltipTarget: e.target,
      tooltipOffset: { x: 16, y: -22 },
    });
  }

  onCompliantBadgeMouseLeave(e: any, node: any, d: any) {
    this.setState({ tooltipText: null });
  }

  linkUpdate(link: any, d: any, i: any, arr: any) {
    d3.select(link)
      .attr("stroke", (d: any) => {
        const parent = this.getEmployee(d.data.parentId);
        return d.data.highlight || (parent && parent.highlight)
          ? "var(--neon-blue-nb-500)"
          : "var(--neon-blue-nb-100)";
      })
      .attr("stroke-width", (d: any) => {
        const parent = this.getEmployee(d.data.parentId);
        return d.data.type === "company" || d.data.type === "nojob-root"
          ? 0
          : d.data.highlight || (parent && parent.highlight)
          ? 2
          : 1;
      })
      .style("opacity", (d) => (!this.searchResults ? "1" : "0.2"));
    //.style('transition', 'stroke 150ms ease');

    if (d.data.highlight) {
      d3.select(link).raise();
    }
  }

  renderButton(args: any) {
    const { node, state } = args;

    if (node.data.type === "root") {
      return "";
    }

    return renderToStaticMarkup(
      <OrgChartButton
        expanded={node.children}
        numChildren={node.data._directSubordinates}
      />
    );
  }

  onMore(e: any, node: any, d: any) {
    this.setState({ actionEmployee: d.data, actionTarget: e.target });
  }

  onCloseActionMenu(e: any) {
    this.setState({ actionTarget: null });
  }

  onViewProfile(e: any) {
    const { onViewProfile } = this.props;
    const { actionEmployee } = this.state;
    if (onViewProfile) onViewProfile(e, actionEmployee);
    this.setState({ actionTarget: null });
  }

  onHighlight(e: any) {
    const { onHighlight } = this.props;
    const { actionEmployee } = this.state;
    //actionEmployee.highlight = !actionEmployee.highlight;

    let timeout = 0;
    /*if (actionEmployee.highlight && !actionEmployee._expanded) {
            this.setExpanded(actionEmployee.employeeId, true);
            this.orgChart.render();
            timeout = 500;
        }*/

    //setTimeout(t => {
    this.setHighlight(actionEmployee.employeeId, !actionEmployee.highlight);

    /*this.orgChart = this.orgChart
            .setExpanded("org-root", true)
            .setExpanded("org-root-job", true)
            .setExpanded(actionEmployee.employeeId, true)
            //.render();*/

    //this.orgChart.updateNodesState();
    this.orgChart.clearHighlighting();

    if (onHighlight) onHighlight(e, actionEmployee);
    this.setState({ actionTarget: null });
    //}, timeout);
  }

  getChildren(id: any) {
    return (Object as any)
      .values(this.props.data.orgTree)
      .filter((e: any) => e.parentId === id);
  }

  getEmployee(employeeId: any) {
    return this.props.data.orgTree.find(
      (e: any) => e.employeeId === employeeId
    );
  }

  setCentered(employeeId: any, centered: any) {
    this.orgChart.setCentered(employeeId, centered);
  }

  setExpanded(employeeId: any, expanded: any) {
    const { data } = this.props;

    this.orgChart = this.orgChart.setExpanded(employeeId, expanded);
    //this.getChildren(employeeId).forEach(e => this.setExpanded(e.employeeId, expanded));

    this.getChildren(employeeId).forEach((e: any) => {
      const node = this.getNode(e.employeeId);
      this.setExpanded(e.employeeId, node.data._expanded === true);
    });
  }

  expandAllParents(employee: any) {
    let parent = this.getEmployee(employee.parentId);
    while (parent) {
      this.setExpanded(parent.employeeId, true);
      parent = this.getEmployee(parent.parentId);
    }

    this.orgChart = this.orgChart
      .setExpanded("org-root", true)
      .setExpanded("org-root-job", true);
  }

  setHighlight(employeeId: any, highlight: any) {
    const employee = this.getEmployee(employeeId);
    const node = this.getNode(employeeId);
    employee.highlight = highlight;
    //this.orgChart = this.orgChart.setExpanded(employeeId, node._expanded);
    this.setExpanded(employeeId, true);
    this.expandAllParents(employee);
  }
  setHighlightSearch = (employeeId: string, highlight: boolean) => {
    try {
      const employee = this.getEmployee(employeeId);
      if (employee) {
        employee.highlight = highlight;
        this.setExpanded(employeeId, true);
        this.orgChart.render(); // Re-render to show highlight
      }
    } catch (e) {
      console.error("Error setting highlight:", e);
    }
  };
  renderNode(d: any, i: any, arr: any, state: any) {
    const { centerOn, organization } = this.props;

    if (d.data.type === "root") return "";
    if (d.data.type === "company") {
      const highlightedChildren = this.getChildren(d.data.id).filter(
        (e: any) => e.highlight
      ).length;
      return renderToStaticMarkup(
        <OrgChartCompanyNode
          key={d.data.id}
          organization={organization}
          data={d.data}
          inHighlightPath={highlightedChildren}
        />
      );
    }
    if (d.data.type === "person") {
      const parent = this.getEmployee(d.data.parentId);
      if (d.data.hasProfilePicture) loadAvatar(d.data.employeeId);
      return renderToStaticMarkup(
        <OrgChartPersonNode
          key={d.data.id}
          data={d.data}
          onMore={this.onMore.bind(this)}
          focused={d.data.highlight}
          inHighlightPath={d.data.highlight || (parent && parent.highlight)}
        />
      );
    }
    if (d.data.type === "nojob-root") {
      return null; //'<div style="text-align:center">Unassigned</div>'
    }
  }

  onZoomOut(e: any) {
    if (!this.orgChart) return;
    this.orgChart.zoomOut();
    this.zoom -= 1.3;
  }

  getNode(nodeId: any) {
    const { data } = this.props;

    if (!data || !this.orgChart) return;
    const { allNodes } = this.orgChart.getChartState();
    if (!allNodes) return;

    return allNodes.filter((d: any) => d.data.id === nodeId)[0];
  }

  onZoomIn(e: any) {
    if (!this.orgChart) return;
    this.orgChart.zoomIn();
    this.zoom += 1.3;
  }

  setZoom() {
    if (!this.orgChart) return;

    const { svg, zoomBehavior } = this.orgChart.getChartState();
    svg.transition().duration(0).call(zoomBehavior.scaleTo, 1);
  }

  render() {
    const { currentUser, data, translations } = this.props;
    const {
      actionTarget,
      actionEmployee,
      tooltipText,
      tooltipTarget,
      tooltipOffset,
      searchAnchorEl,
      searchValue,
    } = this.state;

    //if (!actionTarget)
    if (this.firstDraw) {
      this.renderOrgChart();
    }

    let rootChildren = null;
    if (data) rootChildren = this.getChildren("org-root-job");

    if (rootChildren && rootChildren.length === 0)
      return (
        <div className="klayo-orgchart">
          <Placeholder
            text={{
              primary: translations.orgChartNotAvailable,
              secondary: translations.orgChartNotAvailableDescription,
            }}
            icon={<OrgChartIcon />}
            sx={{ marginTop: "20%" }}
          />
        </div>
      );

    if (this.isSafari)
      return (
        <div className="klayo-orgchart">
          <Placeholder
            text={{
              primary: translations.incompatibleBrowser,
              secondary: translations.incompatibleBrowserDescription,
            }}
            icon={<WarningIcon />}
            sx={{ marginTop: "20%" }}
          />
        </div>
      );
    const employeeOptions: EmployeeOption[] = data
      ? data.employees
          .filter((emp: any) => !emp.isVacancy)
          .map((emp: any) => ({
            employeeId: emp.employeeId,
            label: emp.fullName || "",
          }))
      : [];

    return (
      <div className="klayo-orgchart" style={{ width: "100%", height: "100%" }}>
        <Box
          className="klayo-orgchart_controls"
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            margin: {
              xs: "16px",
              sm: "32px",
              md: "32px",
              lg: "48px",
              xl: "48px",
            },
          }}
        >
          <ButtonGroup
            size="sm"
            buttons={[
              {
                ref: this.searchRef,
                icon: <SearchIcon />,
                onClick: this.handleSearchClick,
              },
              {
                ref: this.zoomOutRef,
                icon: <ZoomOutIcon />,
                //tooltip: 'Zoom out',
                toolTipEnterDelay: 500,
                onClick: this.onZoomOut.bind(this),
              },
              {
                ref: this.resetRef,
                icon: <CenterIcon />,
                disabled: true,
                //tooltip: 'Re-center',
                toolTipEnterDelay: 500,
                onClick: this.onReset.bind(this),
              },
              {
                ref: this.zoomInRef,
                icon: <ZoomInIcon />,
                //tooltip: 'Zoom in',
                toolTipEnterDelay: 500,
                onClick: this.onZoomIn.bind(this),
              },
            ]}
            sx={{ boxShadow: "rgb(248 248 254) 0px 0px 17px 20px" }}
          />
        </Box>
        <Popover
          open={Boolean(searchAnchorEl)}
          anchorEl={searchAnchorEl}
          onClose={this.handleSearchClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 3, width: 400 }}>
            <Autocomplete
              options={employeeOptions}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  className={
                    "klayo-textfield " +
                    "klayo-textfield-dense " +
                    "klayo-textfield--autofilled"
                  }
                  size="small"
                  placeholder={translations.searchEmployees}
                  autoFocus
                />
              )}
              onChange={this.handleEmployeeSelect}
              value={this.state.selectedEmployee}
              isOptionEqualToValue={(option: any, value: any) =>
                option.employeeId === value.employeeId
              }
              autoComplete
              autoHighlight
              clearOnBlur={false}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: Theme.getStyleVar("--neon-blue-nb-200"),
                  },
                  "&:hover fieldset": {
                    borderColor: Theme.getStyleVar("--neon-blue-nb-300"),
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: "2px",
                    borderColor: Theme.getStyleVar("--neon-blue-nb-500"),
                  },
                },
              }}
              renderOption={(props, option, state) => {
                const matches = match(option.label, state.inputValue, {
                  insideWords: true,
                  requireMatchAll: false,
                });
                const parts = parse(option.label, matches);

                return (
                  <li {...props}>
                    {parts.length === 1
                      ? option.label
                      : parts.map((part, index) => (
                          <span
                            key={index}
                            className={
                              "klayo-selector_itempart" +
                              (part.highlight === true
                                ? " klayo-selector_highlight"
                                : "")
                            }
                          >
                            {part.text}
                          </span>
                        ))}
                  </li>
                );
              }}
            />
          </Box>
        </Popover>
        <Box
          ref={this.containerRef}
          sx={{
            width: "100%",
            overflow: "visible",
            height: "100%",
            "&>svg": {
              width: "100%",
              height: "100%",
            },
          }}
        ></Box>

        {tooltipText && (
          <Tooltip
            open={true}
            title={tooltipText}
            anchorEl={tooltipTarget}
            placement="right"
            sx={{
              position: "absolute",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              marginLeft: tooltipOffset.x + "px!important",
              marginTop: tooltipOffset.y + "px",
            }}
          >
            <div></div>
          </Tooltip>
        )}

        <Menu
          open={actionTarget !== null}
          anchorEl={actionTarget}
          onClose={this.onCloseActionMenu.bind(this)}
        >
          <MenuItem onClick={this.onHighlight.bind(this)}>
            {actionEmployee && actionEmployee.highlight
              ? translations.removeHighlight
              : translations.highlightConnections}
          </MenuItem>
          {actionEmployee && actionEmployee.isMyTeam && (
            <MenuItem onClick={this.onViewProfile.bind(this)}>
              {translations.viewEmployeeProfile}
            </MenuItem>
          )}
        </Menu>
      </div>
    );
  }
}
